import { create } from 'zustand';
import type { PositionStructureIntent } from '../models/PositionStructureIntent';

interface PositionStructureOptimisticOperation {
  readonly folderId: string;
  readonly intent: PositionStructureIntent;
  readonly expectedGraphVersion: number | null;
}

interface PositionStructureOptimisticState {
  operationsByFolder: ReadonlyMap<string, PositionStructureOptimisticOperation>;
  begin(folderId: string, intent: PositionStructureIntent): void;
  markCommitted(folderId: string, intentId: string, expectedGraphVersion: number): void;
  clear(folderId?: string, intentId?: string): void;
}

/**
 * Disposable visual feedback for DB-owned position structure. This store is
 * intentionally tab-local and never synced through Yjs or BroadcastChannel.
 */
export const usePositionStructureOptimisticStore = create<PositionStructureOptimisticState>((set) => ({
  operationsByFolder: new Map(),
  begin: (folderId, intent) => set((state) => {
    const next = new Map(state.operationsByFolder);
    next.set(folderId, { folderId, intent, expectedGraphVersion: null });
    return { operationsByFolder: next };
  }),
  markCommitted: (folderId, intentId, expectedGraphVersion) => set((state) => {
    const current = state.operationsByFolder.get(folderId);
    if (current?.intent.intentId !== intentId) return state;
    const next = new Map(state.operationsByFolder);
    next.set(folderId, { ...current, expectedGraphVersion });
    return { operationsByFolder: next };
  }),
  clear: (folderId, intentId) => set((state) => {
    if (!folderId) {
      return state.operationsByFolder.size > 0
        ? { operationsByFolder: new Map() }
        : state;
    }
    const current = state.operationsByFolder.get(folderId);
    if (!current || (intentId && current.intent.intentId !== intentId)) return state;
    const next = new Map(state.operationsByFolder);
    next.delete(folderId);
    return { operationsByFolder: next };
  }),
}));
